import React, { useState, useEffect } from 'react';
import { InputText, DropdownField, ImageUpload, ImageUploadHover } from '../../../common/input';
import useFetch from 'core/hooks/api/useFetch';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import useToggleSections from 'core/services/toggleService';
import usePost from 'core/hooks/api/usePost';
import * as Icon from 'react-feather';
import { Link } from 'react-router-dom';
import EditHistoryModal from 'components/admin/common/modal/EditHistoryModal';
import { validateForm } from 'core/utils/validation';
import { Alert, Modal } from 'react-bootstrap';
import AboutContent from 'components/views/about-artectum/about-content';
import { iconOptions } from 'core/config/iconOptions';

const AboutArtectum = () => {
    const { data, loading: loadingData, error, refetch } = useFetch(API_ENDPOINTS.ABOUT);
    const { postData } = usePost(API_ENDPOINTS.ABOUT);
    const [aboutData, setAboutData] = useState([]);
    const [selectedHistory, setSelectedHistory] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const handlePreviewClose = () => setShowPreview(false);
    const handlePreviewShow = () => setShowPreview(true);

    const [sections, toggleSection] = useToggleSections({
        general: true,
        features: false,
        history: false
    });

    useEffect(() => {
        if (data) {
            setAboutData({
                ...data,
                features: data.features || [],
                history: data.history || []
            });
        }
    }, [data]);

    const handleChange = (key, value) => {
        setAboutData(prevState => ({
            ...prevState,
            [key]: value
        }));
    };

    const handleFeatureChange = (index, key, value) => {
        const newFeatures = [...aboutData.features];
        newFeatures[index][key] = value;
        handleChange('features', newFeatures);
    };

    const addFeature = () => {
        handleChange('features', [...(aboutData.features || []), { icon: "", text: "" }]);
    };

    const removeFeature = (index) => {
        handleChange('features', aboutData.features.filter((_, i) => i !== index));
    };

    const handleHistoryChange = (index, key, value) => {
        const newHistory = [...aboutData.history];
        newHistory[index][key] = value;
        handleChange('history', newHistory);
    };

    const addHistory = () => {
        setShowHistoryModal(true);
        setSelectedHistory({ target: "", image: "", icon: "", subtitle: "", title: "", description: "", details: "" });
    };

    const removeHistory = (index) => {
        handleChange('history', aboutData.history.filter((_, i) => i !== index));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccess(false);
        const validationRules = [
            { field: 'title', method: 'required' },
            { field: 'subtitle', method: 'required' },
            { field: 'description', method: 'required' },
            { field: 'callout', method: 'required' },
            { field: 'buttonText', method: ['required', 'validUrlCharacters'] },
            { field: 'videoId', method: 'required' },
            { field: 'image', method: 'imageFormat' },
            ...aboutData.features.map((_, index) => ({ field: `features.${index}.icon`, method: 'required' })),
            ...aboutData.features.map((_, index) => ({ field: `features.${index}.text`, method: 'required' })),
            ...aboutData.history.map((_, index) => ({ field: `history.${index}.target`, method: 'required' })),
            ...aboutData.history.map((_, index) => ({ field: `history.${index}.title`, method: 'required' })),
            ...aboutData.history.map((_, index) => ({ field: `history.${index}.subtitle`, method: 'required' })),
            ...aboutData.history.map((_, index) => ({ field: `history.${index}.details`, method: 'required' })),
            ...aboutData.history.map((_, index) => ({ field: `history.${index}.icon`, method: 'required' })),
            ...aboutData.history.map((_, index) => ({ field: `history.${index}.image`, method: 'imageFormat' }))
        ];

        const validationErrors = validateForm(aboutData, validationRules);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return;
        }

        try {
            await postData(aboutData, true);
            setSuccess(true);
            refetch();
        } catch (err) {
            console.error('Error updating data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditHistoryClick = (history) => {
        setSelectedHistory(history);
        setShowHistoryModal(true);
    };

    const handleHistoryModalClose = () => {
        setShowHistoryModal(false);
        setSelectedHistory(null);
    };

    const handleHistoryModalSave = (updatedHistory) => {
        const validationRules = [
            { field: 'target', method: 'required' },
            { field: 'title', method: 'required' },
            { field: 'subtitle', method: 'required' },
            { field: 'details', method: 'required' },
            { field: 'icon', method: 'required' },
            { field: 'image', method: 'imageFormat' }
        ];

        const validationErrors = validateForm(updatedHistory, validationRules);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        const updatedHistories = selectedHistory.target
            ? aboutData.history.map((history) =>
                history.target === selectedHistory.target ? updatedHistory : history
            )
            : [...aboutData.history, updatedHistory];
        handleChange('history', updatedHistories);
        setShowHistoryModal(false);
        setSelectedHistory(null);
    };

    if (error) return <div>Error: {error.message}</div>;

    return (
        <div>
            <form onSubmit={handleSubmit} className="about-container">
                <div className="row w-100 mb-2">
                    <div className="p-0 sub-title Service mb-2">
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <h5 className="m-0 text-title">Acerca de Artectum</h5>
                            <div>
                                <button className="btn btn-outline mt-3 me-2" type="button" onClick={handlePreviewShow}>
                                <i className="fa fa-eye me-2" aria-hidden="true" />
                                    Vista Previa
                                </button>
                                <button className="btn btn-primary mt-3" type="submit" disabled={loading}>
                                    {loading ? 'Actualizando...' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
                </div>
                <section>
                    <button type="button" className="use-section-toggle card-drag select mb-3" onClick={() => toggleSection('general')}>
                        <h6 className="text-subtitle link-primary mb-0 ms-3">Información Principal</h6>
                        <i className={`me-3 fas fa-chevron-${sections.general ? 'up' : 'down'}`} />
                    </button>
                    <div className={`set-section-content ${sections.general ? 'open' : ''}`}>
                        <div className="row">
                            <div className="col-md-6">
                                <InputText value={aboutData.title} error={errors?.title} onChange={(e) => handleChange('title', e.target.value)} label="Título" />
                            </div>
                            <div className="col-md-6">
                                <InputText value={aboutData.subtitle} error={errors.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} label="Subtítulo" />
                            </div>
                            <div className="col-md-6">
                                <InputText value={aboutData.description} error={errors.description} onChange={(e) => handleChange('description', e.target.value)} label="Descripción" />
                            </div>
                            <div className="col-md-6">
                                <InputText value={aboutData.callout} error={errors.callout} onChange={(e) => handleChange('callout', e.target.value)} label="Texto Resaltado" />
                            </div>
                            <div className="col-md-6">
                                <InputText value={aboutData.buttonText} error={errors.buttonText} onChange={(e) => handleChange('buttonText', e.target.value)} label="Link del Botón" />
                            </div>
                            <div className="col-md-2 col-xxl-3">
                                <InputText value={aboutData.videoId} error={errors.videoId} onChange={(e) => handleChange('videoId', e.target.value)} label="Video" />
                            </div>
                            <div className="col-md-4 col-xxl-3">
                                <ImageUpload value={aboutData.image} error={errors.image}  onFileChange={(e) => handleChange('image', e.target.files[0])} label="Imagen" />
                            </div>
                        </div>
                    </div>
                </section>
                <section>
                    <button type="button" className="use-section-toggle card-drag select mb-3" onClick={() => toggleSection('features')}>
                        <h6 className="text-subtitle link-primary mb-0 ms-3">Valores Claves  de Artectum</h6>
                        <i className={`me-3 fas fa-chevron-${sections.features ? 'up' : 'down'}`} />
                    </button>
                    <div className={`set-section-content ${sections.features ? 'open' : ''}`}>
                        <div className="row">
                            {aboutData?.features?.map((feature, index) => (
                                <>
                                    <div key={index} className="col-md-5">
                                        <DropdownField
                                            value={feature.icon}
                                            options={iconOptions}
                                            onChange={(e) => handleFeatureChange(index, 'icon', e.value)}
                                            placeholder="Seleccionar Ícono"
                                            label={`Ícono del Valor ${index + 1}`}
                                            appendTo=""
                                            error={errors[`features.${index}.icon`]}
                                        />
                                    </div>
                                    <div key={index} className="col-md-6">
                                        <InputText
                                            value={feature.text}
                                            onChange={(e) => handleFeatureChange(index, 'text', e.target.value)}
                                            label={`Texto del Valor ${index + 1}`}
                                            error={errors[`features.${index}.text`]}
                                        />
                                    </div>
                                    <div key={index} className="col-md-1">
                                        <div className="d-flex justify-content-end align-items-center h-100">
                                            <i className={feature.icon} style={{ fontSize: '24px', marginRight: '10px' }} />
                                            <Link
                                                to="#"
                                                onClick={() => removeFeature(index)}
                                                className="delete-table delete-button me-2"
                                            >
                                                <Icon.Trash2 className="react-feather-custom" />
                                            </Link>
                                        </div>
                                    </div>
                                </>
                            ))}
                        </div>
                        <button className="btn btn-primary-400 ms-3 mb-3" type="button" onClick={addFeature}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Añadir Valor de la Marca
                        </button>
                    </div>
                </section>
                <section>
                    <button type="button" className="use-section-toggle card-drag select mb-3" onClick={() => toggleSection('history')}>
                        <h6 className="text-subtitle link-primary mb-0 ms-3">Historia de la Empresa</h6>
                        <i className={`me-3 fas fa-chevron-${sections.history ? 'up' : 'down'}`} />
                    </button>
                    <div className={`set-section-content ${sections.history ? 'open' : ''}`}>
                        {aboutData?.history?.map((entry, index) => (
                            <div key={index} className="row">
                                <div className="col-md-2">
                                    <InputText
                                        value={entry.target}
                                        onChange={(e) => handleHistoryChange(index, 'target', e.target.value)}
                                        label={`Meta de la Historia ${index + 1}`}
                                        error={errors[`history.${index}.target`]}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <InputText
                                        value={entry.title}
                                        onChange={(e) => handleHistoryChange(index, 'title', e.target.value)}
                                        label={`Título de la Historia ${index + 1}`}
                                        error={errors[`history.${index}.title`]}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <InputText
                                        value={entry.subtitle}
                                        onChange={(e) => handleHistoryChange(index, 'subtitle', e.target.value)}
                                        label={`Subtítulo de la Historia ${index + 1}`}
                                        error={errors[`history.${index}.subtitle`]}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <ImageUploadHover
                                        value={entry.image}
                                        onFileChange={(e) => handleHistoryChange(index, 'image', e.target.files[0])}
                                        label={`Imagen de la Historia ${index + 1}`}
                                        error={errors[`history.${index}.image`]}
                                    />
                                </div>
                                <div className="col-md-2">
                                <div className="d-flex justify-content-end align-items-end">
                                    <Link
                                        to="#"
                                        onClick={() => handleEditHistoryClick(entry)}
                                        className="delete-table me-2"
                                    >
                                        <Icon.Edit className="react-feather-custom" />
                                    </Link>
                                    <Link
                                        to="#"
                                        onClick={() => removeHistory(index)}
                                        className="delete-table delete-button me-2"
                                    >
                                        <Icon.Trash2 className="react-feather-custom" />
                                    </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button className="btn btn-primary-400 ms-3" type="button" onClick={addHistory}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Añadir Historia
                        </button>
            {success && <Alert className="mt-3" variant="success">Datos actualizados con éxito.</Alert>}
                    </div>
                </section>
            </form>
                <EditHistoryModal
                    show={showHistoryModal}
                    history={selectedHistory}
                    handleClose={handleHistoryModalClose}
                    handleSave={handleHistoryModalSave}
                />
            <Modal show={showPreview} onHide={handlePreviewClose} size="xxl">
                <Modal.Header className="pb-0" closeButton style={{ padding: '30px'}}>
                    <Modal.Title className="text-title">Vista Previa del Acerca de Nosotros</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-0">
                    <AboutContent data={aboutData} preview={false} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AboutArtectum;